/**
 * The tenant's display settings, carried to every screen that writes a date or
 * a number.
 *
 * WHY ONE CONTEXT AND NOT TWO
 *   A date format and a number grouping are two answers to the same question —
 *   how does this tenant write things down — and they arrive together on the
 *   one `/v1/me/tenant` read that already happens once per session. A second
 *   provider would mean a second context, a second fetch and a second place to
 *   forget a setting, which is the duplication the product forbids.
 *
 * SUPPLYING THEM IS THE HOST'S JOB
 *   This file holds the values and nothing else: no fetching, no storage, no
 *   platform APIs. That is what lets it be bundled by Vite for the admin app
 *   and by Metro for the mobile app, exactly as `I18nProvider` is. The admin
 *   shell feeds it from `useTenantIdentity`; mobile feeds it from
 *   `MobileTenantDisplaySettings`.
 *
 * WITH NO PROVIDER ABOVE IT
 *   The shipped defaults apply — which is what a tenant that has configured
 *   nothing sees anyway, so a screen rendered outside the shell (a test, a
 *   screen not yet adopted) writes exactly what one inside it writes.
 */
import { createContext, createElement, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
// Extensionless on purpose — see useBusinessDay.ts. Metro cannot follow a
// '.js' specifier pointing at a '.ts' file.
import { DEFAULT_BUSINESS_DAY_FORMAT } from '../contracts/business-day';
import { DEFAULT_NUMBER_FORMAT } from '../contracts/business-number';

export interface DisplaySettings {
  /** DISPLAY_DATE_FORMAT — how this tenant writes a business day. */
  readonly dateFormat: string;
  /** DISPLAY_NUMBER_FORMAT — how this tenant groups amounts and quantities. */
  readonly numberFormat: string;
}

export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  dateFormat: DEFAULT_BUSINESS_DAY_FORMAT,
  numberFormat: DEFAULT_NUMBER_FORMAT,
};

const DisplaySettingsContext = createContext<DisplaySettings>(
  DEFAULT_DISPLAY_SETTINGS,
);

export interface DisplaySettingsProviderProps {
  readonly children: ReactNode;
  /** The tenant's DISPLAY_DATE_FORMAT. Absent falls back to the default. */
  readonly dateFormat?: string | null;
  /** The tenant's DISPLAY_NUMBER_FORMAT. Absent falls back to the default. */
  readonly numberFormat?: string | null;
}

/** Publishes the tenant's display settings to everything below it. */
export function DisplaySettingsProvider({
  children,
  dateFormat,
  numberFormat,
}: DisplaySettingsProviderProps): ReactNode {
  const value = useMemo<DisplaySettings>(
    () => ({
      dateFormat: dateFormat ?? DEFAULT_DISPLAY_SETTINGS.dateFormat,
      numberFormat: numberFormat ?? DEFAULT_DISPLAY_SETTINGS.numberFormat,
    }),
    [dateFormat, numberFormat],
  );
  return createElement(
    DisplaySettingsContext.Provider,
    { value },
    children,
  );
}

/** The tenant's display settings as they stand for this subtree. */
export function useDisplaySettings(): DisplaySettings {
  return useContext(DisplaySettingsContext);
}
